import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';

type Support = 'yes' | 'partial' | 'no';

interface ComparisonRow {
  capability: string;
  detail?: string;
  values: Support[];
}

interface ComparisonTableProps {
  // The first column is treated as the featured model.
  columns: string[];
  rows: ComparisonRow[];
  caption?: string;
}

const supportLabels: Record<Support, string> = {
  yes: 'Supported',
  partial: 'Limited',
  no: 'Not supported',
};

function SupportCell({ value }: { value: Support }) {
  if (value === 'yes') {
    return (
      <>
        <CheckmarkIcon className="text-primary mx-auto size-5" />
        <span className="sr-only">{supportLabels.yes}</span>
      </>
    );
  }
  return (
    <Text
      as="span"
      variant="web/body/medium/xs"
      className={value === 'partial' ? 'text-secondary' : 'text-tertiary'}
    >
      {value === 'partial' ? (
        supportLabels.partial
      ) : (
        <>
          <span aria-hidden="true">—</span>
          <span className="sr-only">{supportLabels.no}</span>
        </>
      )}
    </Text>
  );
}

export default function ComparisonTable({
  columns,
  rows,
  caption,
}: ComparisonTableProps) {
  return (
    <figure className="article-breakout my-8 px-4 lg:my-12 lg:px-0">
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr>
              <th scope="col" className="w-[40%] pb-4">
                <span className="sr-only">Capability</span>
              </th>
              {columns.map((column, index) => (
                <th
                  key={column}
                  scope="col"
                  className={`px-3 pb-4 pt-4 text-center align-bottom ${index === 0 ? 'bg-secondary rounded-t-2xl' : ''}`}
                >
                  <Text
                    as="span"
                    variant="web/body/medium/md"
                    className={index === 0 ? 'text-primary' : 'text-secondary'}
                  >
                    {column}
                  </Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const isLast = rowIndex === rows.length - 1;
              return (
                <tr key={row.capability} className="border-primary border-t">
                  <th scope="row" className="py-4 pr-6 align-top font-normal">
                    <Text
                      as="span"
                      variant="web/body/medium/md"
                      className="text-primary block"
                    >
                      {row.capability}
                    </Text>
                    {row.detail && (
                      <Text
                        as="span"
                        variant="web/body/medium/xs"
                        className="text-tertiary mt-1 block"
                      >
                        {row.detail}
                      </Text>
                    )}
                  </th>
                  {row.values.map((value, index) => (
                    <td
                      key={columns[index]}
                      className={`px-3 py-4 text-center align-middle ${index === 0 ? `bg-secondary ${isLast ? 'rounded-b-2xl' : ''}` : ''}`}
                    >
                      <SupportCell value={value} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {caption && (
        <figcaption className="article-caption mx-auto mt-3 px-4">
          <Text
            as="span"
            variant="web/body/medium/xs"
            className="text-tertiary"
          >
            {caption}
          </Text>
        </figcaption>
      )}
    </figure>
  );
}
